import type { Cart, CartValidationStatus, CommerceClient } from '@altazion/commerce-sdk-core'

import {
  asRecord,
  requireNumber,
  requireString,
  type AltazionModuleDefinition,
  type AltazionSuccessEventKind,
} from '../altazion-engine'

export type AltazionCartActionName =
  | 'getCart'
  | 'getValidationStatus'
  | 'addItem'
  | 'updateItem'
  | 'removeItem'
  | 'applyCoupon'
  | 'removeCoupon'

export type CartActionResult = Cart | CartValidationStatus

export const cartModuleDefinition: AltazionModuleDefinition<'cart', AltazionCartActionName, CartActionResult> = {
  moduleName: 'cart',
  actionAttribute: 'hx-altazion-cart-action',
  payloadAttributes: {
    'hx-altazion-cart-reference': 'reference',
    'hx-altazion-cart-quantity': 'quantity',
    'hx-altazion-cart-line-id': 'lineId',
    'hx-altazion-cart-code': 'code',
  },
  jsonPayloadAttributes: {
    'hx-altazion-cart-options': 'options',
  },
  payloadAliases: {
    ref: 'reference',
    productReference: 'reference',
    qty: 'quantity',
    line: 'lineId',
    coupon: 'code',
  },
  parseAction(actionValue: string): AltazionCartActionName {
    switch (actionValue) {
      case 'getCart':
      case 'getValidationStatus':
      case 'addItem':
      case 'updateItem':
      case 'removeItem':
      case 'applyCoupon':
      case 'removeCoupon':
        return actionValue
      default:
        throw new Error(`Unsupported cart action: ${actionValue}`)
    }
  },
  execute(client: CommerceClient, action: AltazionCartActionName, payload: Record<string, unknown>): Promise<CartActionResult> {
    switch (action) {
      case 'getCart':
        return client.cart.getCart()
      case 'getValidationStatus':
        return client.cart.getValidationStatus()
      case 'addItem': {
        const reference = requireString(payload.reference, 'reference')
        const quantity = requireNumber(payload.quantity, 'quantity')
        const options = buildAddItemOptions(payload)
        return client.cart.addItem(reference, quantity, options)
      }
      case 'updateItem': {
        const lineId = requireString(payload.lineId, 'lineId')
        const quantity = requireNumber(payload.quantity, 'quantity')
        return client.cart.updateItem(lineId, quantity)
      }
      case 'removeItem':
        return client.cart.removeItem(requireString(payload.lineId, 'lineId'))
      case 'applyCoupon':
        return client.cart.applyCoupon(requireString(payload.code, 'code'))
      case 'removeCoupon':
        return client.cart.removeCoupon(requireString(payload.code, 'code'))
    }
  },
  successEventKind(action: AltazionCartActionName): AltazionSuccessEventKind {
    switch (action) {
      case 'getCart':
        return 'loaded'
      case 'getValidationStatus':
        return 'none'
      case 'addItem':
      case 'updateItem':
      case 'removeItem':
      case 'applyCoupon':
      case 'removeCoupon':
        return 'updated'
    }
  },
  getResultEventSuffixes(_action: AltazionCartActionName, result: CartActionResult): string[] {
    return isCart(result) ? ['updated'] : ['status']
  },
}

function buildAddItemOptions(payload: Record<string, unknown>): Record<string, unknown> | undefined {
  const options = asRecord(payload.options)
  const extraEntries = Object.entries(payload).filter(([key]) => ![
    'reference',
    'ref',
    'productReference',
    'quantity',
    'qty',
    'options',
  ].includes(key))

  if (extraEntries.length === 0 && !options) {
    return undefined
  }

  return {
    ...Object.fromEntries(extraEntries),
    ...options,
  }
}

function isCart(result: CartActionResult): result is Cart {
  return 'guid' in result
}